import { useCallback, useState } from 'react';
import { mediaApi, type MediaAsset, type MediaUploadMeta } from '../../services/mediaService';

export interface UploadZeile {
  key: string;
  name: string;
  /** 0..1 */
  anteil: number;
  status: 'laeuft' | 'fertig' | 'doppelt' | 'fehler';
  fehler?: string;
}

/**
 * Laedt mehrere Dateien nacheinander hoch (ein grosses Video soll nicht mit
 * fuenf anderen um die Leitung konkurrieren) und haelt je Datei den
 * Fortschritt fest. `onFertig` laeuft nach jeder erfolgreichen Datei.
 */
export function useMediaUpload(onFertig?: (asset: MediaAsset) => void) {
  const [zeilen, setZeilen] = useState<UploadZeile[]>([]);
  const [aktiv, setAktiv] = useState(false);

  const setze = (key: string, teil: Partial<UploadZeile>) =>
    setZeilen((alt) => alt.map((z) => (z.key === key ? { ...z, ...teil } : z)));

  const hochladen = useCallback(async (dateien: File[], meta: MediaUploadMeta = {}) => {
    if (dateien.length === 0) return;
    const neu = dateien.map((d, i) => ({
      key: `${Date.now()}-${i}-${d.name}`, name: d.name, anteil: 0, status: 'laeuft' as const,
    }));
    setZeilen((alt) => [...neu, ...alt.filter((z) => z.status === 'laeuft')]);
    setAktiv(true);
    try {
      for (let i = 0; i < dateien.length; i++) {
        const { key } = neu[i];
        try {
          const asset = await mediaApi.upload(dateien[i], meta, (anteil) => setze(key, { anteil }));
          setze(key, { anteil: 1, status: asset?.duplicate ? 'doppelt' : 'fertig' });
          if (asset) onFertig?.(asset);
        } catch (err: any) {
          setze(key, { status: 'fehler', fehler: err?.message || 'Hochladen fehlgeschlagen' });
        }
      }
    } finally {
      setAktiv(false);
    }
  }, [onFertig]);

  const leeren = useCallback(() => setZeilen((alt) => alt.filter((z) => z.status === 'laeuft')), []);

  return { zeilen, aktiv, hochladen, leeren };
}

const STATUS_TEXT: Record<UploadZeile['status'], string> = {
  laeuft: '',
  fertig: 'fertig',
  doppelt: 'war schon vorhanden',
  fehler: 'Fehler',
};

export function uploadStatusText(z: UploadZeile): string {
  if (z.status === 'laeuft') return `${Math.round(z.anteil * 100)} %`;
  if (z.status === 'fehler') return z.fehler || STATUS_TEXT.fehler;
  return STATUS_TEXT[z.status];
}
